import  React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import InputDateRange from '../InputDateRange/InputDateRange'
import SidebarModal from '../SidebarModal/SidebarModal'
import './SessionFilter.scss'
import InputCheckbox from '../InputCheckbox/InputCheckbox'
import CloseIcon from '../Icons/CloseIcon'
import { getDefaultFilter, useFilterContext, Filter, getNumberRangeErrors } from '../../contexts/filter-context'
import InputText from '../InputText/InputText'
import FilterIcon from '../Icons/FilterIcon'
import InputNumber from '../InputNumber/InputNumber'
import { IcaAlertMessage } from '../IcaElements/IcaAlertMessage'

const INPUT_UNITS = {
  duration: 'min',
  price: 'kr',
  itemCount: 'st',
  weight: 'g',
} as const

type FilterName =
  | 'minSessionDurationMinutes'
  | 'maxSessionDurationMinutes'
  | 'minPriceKr'
  | 'maxPriceKr'
  | 'minItemCount'
  | 'maxItemCount'
  | 'minCustomerWeighedItemGrams'
  | 'maxCustomerWeighedItemGrams'
  | 'excludeEmptySessionsNearPurchase'

export default function SessionFilter () {
  const location = useLocation()
  const { filterState, filterReducer } = useFilterContext()
  const [displayFilterModal, setDisplayFilterModal] = useState<boolean>(false)
  const filterButtonDefaultMsg = 'Visa alla'
  const [filterButtonMsg, setFilterButtonMsg] = useState<string>(filterButtonDefaultMsg)
  const rangeErrors = getNumberRangeErrors(filterState.filter)

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    switch (event.target.type) {
      case 'checkbox':
        filterReducer.setPropertyValue(event.target.name, event.target.checked)    
        break
      default:
        filterReducer.setPropertyValue(event.target.name, event.target.value)    
        break
    }
  }

  const handleNumberChange = (
    name: FilterName,
    value: string
  ) => {
    if (value === '') {
      filterReducer.setPropertyValue(name, null)
      return
    }

    const numberValue = Number(value)
    if (!Number.isSafeInteger(numberValue)) return

    filterReducer.setPropertyValue(name, numberValue)
  }

  const handleModalReset = () => {
    const newFilter: Filter = getDefaultFilter()
    newFilter.storeId = filterState.filter.storeId
    newFilter.from = filterState.filter.from
    newFilter.to = filterState.filter.to
    filterReducer.setFilter(newFilter)
  }

  const getActiveFilterCount = (): number => {
    let activeFilterCount: number = 0
    const defaultFilter = getDefaultFilter()

    if (filterState.filter.paymentMissing != defaultFilter.paymentMissing) activeFilterCount++
    if (filterState.filter.multiplePayments != defaultFilter.multiplePayments) activeFilterCount++
    if (filterState.filter.removedItems != defaultFilter.removedItems) activeFilterCount++
    if (filterState.filter.accessDeniedBanned != defaultFilter.accessDeniedBanned) activeFilterCount++
    if (filterState.filter.accessDeniedTermsNotMet != defaultFilter.accessDeniedTermsNotMet) activeFilterCount++
    if (filterState.filter.unviewedSessions != defaultFilter.unviewedSessions) activeFilterCount++
    if (filterState.filter.flagged != defaultFilter.flagged) activeFilterCount++
    if (filterState.filter.excludeEmptySessionsNearPurchase != defaultFilter.excludeEmptySessionsNearPurchase) activeFilterCount++

    if (filterState.filter.minSessionDurationMinutes != defaultFilter.minSessionDurationMinutes
      || filterState.filter.maxSessionDurationMinutes != defaultFilter.maxSessionDurationMinutes)
      activeFilterCount++
    
    if (filterState.filter.minPriceKr != defaultFilter.minPriceKr
      || filterState.filter.maxPriceKr != defaultFilter.maxPriceKr)
      activeFilterCount++
    
    if (filterState.filter.minItemCount != defaultFilter.minItemCount
      || filterState.filter.maxItemCount != defaultFilter.maxItemCount)
      activeFilterCount++
      
    if (filterState.filter.minCustomerWeighedItemGrams != defaultFilter.minCustomerWeighedItemGrams
      || filterState.filter.maxCustomerWeighedItemGrams != defaultFilter.maxCustomerWeighedItemGrams)
      activeFilterCount++

    return activeFilterCount
  }

  React.useEffect(() => {
    const activeFilterCount = getActiveFilterCount()
    if (activeFilterCount == 0) {
      setFilterButtonMsg(filterButtonDefaultMsg)
    }
    else if (activeFilterCount == 1) {
      setFilterButtonMsg(`${activeFilterCount} vald`)
    }
    else {
      setFilterButtonMsg(`${activeFilterCount} valda`)
    }
  }, [filterState.filter])

  return (
    <div className='filter-container'>
      <InputDateRange
        onFromChange={(from) => filterReducer.setPropertyValue('from', from)}
        onToChange={(to) => filterReducer.setPropertyValue('to', to)}
        defaultDateRange={{ from: filterState.filter.from, to: filterState.filter.to }}
      />
      <div>
        {location.pathname.includes('/sessions')
          ? <>
            <div className='md:mt-2 mb-2'>
              <label htmlFor='filter-input-button' className='select-none inline-block mb-1'>Filter</label>
              <InputText
                value={filterButtonMsg}
                icon={<FilterIcon />} 
                onChange={() => null}
                onClick={() => setDisplayFilterModal(!displayFilterModal)}
                className={`cursor-pointer outline-hidden${getActiveFilterCount() > 0 ? ' filter-button-active' : ''}`}
                id='filter-input-button'
                readonly />
            </div>
            <SidebarModal show={displayFilterModal}>
              <div className='border-bottom flex py-3 px-5'>
                <div className='filter-title select-none'>Filtrera</div>
                <div className='close-button ml-auto' onClick={() => setDisplayFilterModal(false)}>
                  <CloseIcon size='20px' />
                </div>
              </div>
              <div className='px-5 mb-3'>
                <div className='filter-subtitle mt-6'>Kundvarv</div>
                <hr className='mt-4'/>
                <InputCheckbox label='Saknar betalning' id='filter-payment-missing' name='paymentMissing' checked={!!filterState.filter.paymentMissing} onCheckChanged={handleInputChange} className='py-4' />
                <hr/>
                <InputCheckbox label='Flera betalningar' id='filter-multiple-payments' name='multiplePayments' checked={!!filterState.filter.multiplePayments} onCheckChanged={handleInputChange} className='py-4' />
                <hr/>
                <InputCheckbox label='Borttagning av vara' id='filter-removed-items' name='removedItems' checked={!!filterState.filter.removedItems} onCheckChanged={handleInputChange} className='py-4' />
                <hr/>
                <InputCheckbox label='Spärrad' id='filter-access-denied' name='accessDeniedBanned' checked={!!filterState.filter.accessDeniedBanned} onCheckChanged={handleInputChange} className='py-4' />
                <hr/>
                <InputCheckbox label='Nekad (Uppfyller ej krav)' id='filter-access-denied-terms-not-met' name='accessDeniedTermsNotMet' checked={!!filterState.filter.accessDeniedTermsNotMet} onCheckChanged={handleInputChange} className='py-4' />
                <hr/>
                <InputCheckbox label='Ej granskad' id='filter-unviewed-sessions' name='unviewedSessions' checked={!!filterState.filter.unviewedSessions} onCheckChanged={handleInputChange} className='py-4' />
                <hr/>
                <InputCheckbox label='Flaggad' id='filter-flagged' name='flagged' checked={!!filterState.filter.flagged} onCheckChanged={handleInputChange} className='py-4' />
                <hr />
                <InputCheckbox label='Dölj brus' id='filter-exclude-empty-sessions' name='excludeEmptySessionsNearPurchase' checked={!!filterState.filter.excludeEmptySessionsNearPurchase} onCheckChanged={handleInputChange} className='py-4' />
                <hr className='mb-4'/>
                <div className='filter-subtitle mt-4 mb-2'>Besökslängd</div>
                <div className='flex gap-4 mb-2 w-full'>
                  <InputNumber
                    id='filter-min-duration'
                    ariaLabel='Minsta besökslängd i minuter'
                    unit={INPUT_UNITS.duration}
                    label='Min'
                    value={String(filterState.filter.minSessionDurationMinutes ?? '')}
                    onBlur={(value) =>
                      handleNumberChange('minSessionDurationMinutes', value)
                    }
                  />
                  <InputNumber
                    id='filter-max-duration'
                    ariaLabel='Max besökslängd i minuter'
                    unit={INPUT_UNITS.duration}
                    label='Max'
                    value={String(filterState.filter.maxSessionDurationMinutes ?? '')}
                    onBlur={(value) =>
                      handleNumberChange('maxSessionDurationMinutes', value)
                    }
                  />
                </div>
                {rangeErrors.duration && (
                  <IcaAlertMessage
                    alarmtype='failure'
                    text='Minsta besökslängd kan inte vara större än max.'
                    persistencetype='persistent'
                    noclose={true}
                  />
                )}
                <div className='filter-subtitle mt-4 mb-2'>Betalbelopp</div>
                <div className='flex gap-4 mb-2 w-full'>
                  <InputNumber
                    id='filter-min-price'
                    ariaLabel='Minsta pris i kronor'
                    unit={INPUT_UNITS.price}
                    label='Min'
                    value={String(filterState.filter.minPriceKr ?? '')}
                    onBlur={(value) =>
                      handleNumberChange('minPriceKr', value)
                    }
                  />
                  <InputNumber
                    id='filter-max-price'
                    ariaLabel='Max pris i kronor'
                    unit={INPUT_UNITS.price}
                    label='Max' 
                    value={String(filterState.filter.maxPriceKr ?? '')}
                    onBlur={(value) =>
                      handleNumberChange('maxPriceKr', value)
                    }
                  />
                </div>
                {rangeErrors.price && (
                  <IcaAlertMessage
                    alarmtype='failure'
                    text='Minsta betalbelopp kan inte vara större än max.'
                    persistencetype='persistent'
                    noclose={true}
                  />
                )}
                <div className='filter-subtitle mt-4 mb-2'>Antal varor</div>
                <div className='flex gap-4 mb-2 w-full'>
                  <InputNumber
                    id='filter-min-items'
                    ariaLabel='Minsta antal varor'
                    unit={INPUT_UNITS.itemCount}
                    label='Min'
                    value={String(filterState.filter.minItemCount ?? '')}
                    onBlur={(value) =>
                      handleNumberChange('minItemCount', value)
                    }
                  />
                  <InputNumber
                    id='filter-max-items'
                    ariaLabel='Max antal varor'
                    unit={INPUT_UNITS.itemCount}
                    label='Max'
                    value={String(filterState.filter.maxItemCount ?? '')}
                    onBlur={(value) =>
                      handleNumberChange('maxItemCount', value)
                    }
                  />
                </div>
                {rangeErrors.itemCount && (
                  <IcaAlertMessage
                    alarmtype='failure'
                    text='Minsta antal varor kan inte vara större än max.'
                    persistencetype='persistent'
                    noclose={true}
                  />
                )}
                <div className='filter-subtitle mt-4 mb-2'>Viktvaror</div>
                <div className='flex gap-4 mb-2 w-full'>
                  <InputNumber
                    id='filter-min-weight'
                    ariaLabel='Minsta vikt'
                    unit={INPUT_UNITS.weight}
                    label='Min'
                    value={String(filterState.filter.minCustomerWeighedItemGrams ?? '')}
                    onBlur={(value) =>
                      handleNumberChange('minCustomerWeighedItemGrams', value)
                    }
                  />
                  <InputNumber
                    id='filter-max-weight'
                    ariaLabel='Max vikt'
                    unit={INPUT_UNITS.weight}
                    label='Max'
                    value={String(filterState.filter.maxCustomerWeighedItemGrams ?? '')}
                    onBlur={(value) =>
                      handleNumberChange('maxCustomerWeighedItemGrams', value)
                    }
                  />
                </div>
                {rangeErrors.weight && (
                  <IcaAlertMessage
                    alarmtype='failure'
                    text='Minsta vikt kan inte vara större än max.'
                    persistencetype='persistent'
                    noclose={true}
                  />
                )}
              </div>
              <div className='filter-footer mt-auto border-top sticky bottom-0'>
                <div className='flex p-4 w-full'>
                  <button type="button" className='filter-reset-button button button-secondary grow mr-1 xl:mr-0' onClick={handleModalReset}>Rensa</button>
                  <button type="button" className='filter-reset-button button grow ml-1' onClick={() => setDisplayFilterModal(false)}>Klar</button>
                </div>
              </div>
            </SidebarModal>
          </>
          : <InputCheckbox label='Flaggad' id='filter-flagged-inline' name='flagged' checked={!!filterState.filter.flagged} onCheckChanged={handleInputChange} className='filter p-4' />
        }
      </div>
    </div>
  )
}
